import { Card, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { type ExternalAccountDetailsAttributes } from "@src/appV2/redesign/Accounts/PayoutSettings/api/schema";
import { ExternalAccountLogo } from "@src/appV2/redesign/Accounts/PayoutSettings/components/ExternalAccountLogo";

import {
  CancellationType,
  type CancelledShiftDetailsData,
  PayEligibility,
} from "../../api/cancelledShiftTypes";
import { ShiftInfoRow } from "../../ShiftDetails/components/ShiftInfoRow";
import { StatusPill } from "./StatusPill";

interface PayEligibilityCardProps {
  readonly data: CancelledShiftDetailsData;
  readonly payoutDestination: ExternalAccountDetailsAttributes | undefined;
}

export function PayEligibilityCard({ data, payoutDestination }: PayEligibilityCardProps) {
  const isEligible = data.payEligibility === PayEligibility.ELIGIBLE;
  const isSentHome = data.cancellationType === CancellationType.SENT_HOME;

  return (
    <Card variant="tertiary" sx={(theme) => ({ padding: theme.spacing(5) })}>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Text semibold variant="body1">
            Pay Eligibility
          </Text>
          <StatusPill
            label={isEligible ? "Eligible" : "Not Eligible"}
            variant={isEligible ? "success" : "neutral"}
          />
        </Stack>
        <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
          {getEligibilityDescription(isEligible, isSentHome)}
        </Text>
        {isEligible && (
          <PayDetails
            isSentHome={isSentHome}
            lateCancellationFeeHours={data.lateCancellationFeeHours}
            sentHomeChargeHours={data.sentHomeChargeHours}
            paymentStatus={data.paymentStatus}
            payoutDestination={payoutDestination}
          />
        )}
      </Stack>
    </Card>
  );
}

function getEligibilityDescription(isEligible: boolean, isSentHome: boolean): string {
  if (!isEligible) {
    return "Based on the workplace's cancellation policy, this cancellation is not eligible for pay.";
  }

  if (isSentHome) {
    return "You were sent home and are eligible for cancellation pay per the workplace policy.";
  }

  return "This shift was cancelled within the late cancellation window. You are eligible for cancellation pay.";
}

interface PayDetailsProps {
  readonly isSentHome: boolean;
  readonly lateCancellationFeeHours: number;
  readonly sentHomeChargeHours: number | undefined;
  readonly paymentStatus: CancelledShiftDetailsData["paymentStatus"];
  readonly payoutDestination: ExternalAccountDetailsAttributes | undefined;
}

function PayDetails(props: PayDetailsProps) {
  const {
    isSentHome,
    lateCancellationFeeHours,
    sentHomeChargeHours,
    paymentStatus,
    payoutDestination,
  } = props;

  const payHours = isSentHome ? sentHomeChargeHours : lateCancellationFeeHours;
  const payHoursLabel = isSentHome ? "Sent home pay hours" : "Late cancel pay hours";

  return (
    <Stack spacing={2}>
      {payHours !== undefined && payHours > 0 && (
        <ShiftInfoRow label={payHoursLabel} value={`${payHours}h`} />
      )}
      {paymentStatus?.payoutStatus !== undefined && (
        <ShiftInfoRow
          label="Payment status"
          value={formatPaymentStatus(paymentStatus.payoutStatus)}
        />
      )}
      {isDefined(payoutDestination) && (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
            Payout destination
          </Text>
          <Stack direction="row" alignItems="center" spacing={2}>
            <ExternalAccountLogo externalAccount={payoutDestination} />
            <Text variant="body2">
              {payoutDestination.providerName ?? "Account"} ···· {payoutDestination.last4}
            </Text>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}

function formatPaymentStatus(status: string): string {
  switch (status) {
    case "paid": {
      return "Paid";
    }
    case "pending": {
      return "Pending";
    }
    case "failed": {
      return "Failed";
    }
    default: {
      return status;
    }
  }
}
